import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import type {
	ButtonHTMLAttributes,
	DetailedHTMLProps,
	ForwardRefExoticComponent,
	RefAttributes,
	SVGProps,
} from "react";
import { forwardRef } from "react";
import { useButton } from "react-aria";
import { LoadingSpinner } from "../../feedback/LoadingSpinner";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../overlay/Tooltip";
import { classNames } from "../../utils/classNames";
import { buttonStyles } from "./Button";

const iconButtonStyles = cva(["border", "rounded-lg"], {
	variants: {
		intent: {
			primary: buttonStyles({ intent: "primary", size: "custom" }),
			secondary: `${buttonStyles({ intent: "secondary", size: "custom" })} hover:bg-neutral/10  `,
			tertiary: `${buttonStyles({ intent: "tertiary", size: "custom" })} hover:bg-neutral/10`,
			flat: `${buttonStyles({ intent: "flat", size: "custom" })} hover:bg-neutral/10`,
			action: `${buttonStyles({ intent: "action", size: "custom" })} hover:bg-accent/10`,
		},
		size: {
			custom: [],
			xs: ["text-xs", "min-w-[28px]", "min-h-[28px]", "h-7", "line-height-0"],
			sm: ["text-sm", "min-w-[32px]", "min-h-[32px]", "h-8", "line-height-0"],
			md: ["text-sm", "min-w-[36px]", "min-h-[36px]", "h-9", "line-height-0"],
			lg: ["text-md", "min-w-[38px]", "min-h-[38px]", "h-10", "line-height-0"],
		},
		isDestructive: {
			destructive: [""],
		},
		isActive: {
			true: "",
			false: "",
		},
	},
	compoundVariants: [
		{
			intent: "primary",
			isDestructive: "destructive",
			className: "bg-critical text-on-critical hover:bg-[rgb(var(--os-color-critical-700))]",
		},
		{
			intent: "secondary",
			isDestructive: "destructive",
			className: "text-critical hover:text-critical-hover",
		},
		{
			intent: "tertiary",
			isDestructive: "destructive",
			className: "text-neutral-faded hover:text-neutral",
		},
		{
			intent: "flat",
			isActive: true,
			className: "bg-button-flat-hover",
		},
	],
	defaultVariants: {
		intent: "primary",
		size: "md",
	},
});

const iconSize = {
	custom: "",
	xs: "h-4 w-4",
	sm: "h-5 w-5",
	md: "h-5 w-5",
	lg: "h-6 w-6",
};

const iconButtonSpinnerClass = (
	intent: VariantProps<typeof iconButtonStyles>["intent"],
	isDestructive: boolean | undefined,
): string => {
	if (!isDestructive) {
		return "text-primary";
	}
	if (intent === "primary") {
		return "text-white";
	}
	if (intent === "tertiary") {
		return "text-neutral-faded";
	}
	return "text-critical";
};

export type IconButtonProps = Omit<
	DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>,
	"children" | "disabled"
> & {
	intent?: VariantProps<typeof iconButtonStyles>["intent"];
	size?: VariantProps<typeof iconButtonStyles>["size"];
	"aria-label": string;
	icon: ForwardRefExoticComponent<
		Omit<SVGProps<SVGSVGElement>, "ref"> & {
			title?: string | undefined;
			titleId?: string | undefined;
		} & RefAttributes<SVGSVGElement>
	>;
	isDestructive?: boolean;
	isActive?: boolean;
	isDisabled?: boolean;
	isLoading?: boolean;
	tooltip?: boolean | string;
};

const IconButtonInner = (props: IconButtonProps, ref: React.ForwardedRef<HTMLButtonElement>) => {
	const {
		className,
		size = "md",
		intent = "primary",
		isDestructive,
		isActive,
		isDisabled,
		isLoading,
		tooltip = true,
		...rest
	} = props;

	// @ts-expect-error React Aria uses Element instead of HTMlButtonElement, shouldn't impact anything in runtime
	const { buttonProps } = useButton({ ...rest, isDisabled: rest.isDisabled }, ref);

	const button = (
		<button
			ref={ref}
			{...buttonProps}
			className={classNames(
				iconButtonStyles({
					intent,
					size,
					isDestructive: isDestructive ? "destructive" : undefined,
					isActive,
				}),
				className,
			)}
			disabled={isDisabled}
		>
			{isLoading ? (
				<LoadingSpinner className={iconButtonSpinnerClass(intent, isDestructive)} />
			) : (
				<props.icon className={classNames(iconSize[size ?? "md"])} />
			)}
		</button>
	);

	const tooltipText = typeof tooltip === "string" ? tooltip : props["aria-label"];

	if (!tooltip || !tooltipText) {
		return button;
	}

	return (
		<Tooltip delay={{ open: 300 }}>
			<TooltipTrigger>{button}</TooltipTrigger>
			<TooltipContent>{tooltipText}</TooltipContent>
		</Tooltip>
	);
};

IconButtonInner.displayName = "IconButton";

export const IconButton = forwardRef(IconButtonInner);
